import api from './api';

export type CompanyGroup = '세아홀딩스 계열' | '세아제강지주 계열';
export type TrafficType = 'web' | 'netapp';
export type AccountType = 'personal' | 'shared';

export interface WfDetailException {
  id: number;
  requestNo?: number | null;
  exceptGroup?: string | null;
  exceptDetail?: string | null;
  companyGroup?: string | null;
  reqCompany?: string | null;
  reqDepartment?: string | null;
  reqName?: string | null;
  targetCompany?: string | null;
  permittedIp?: string | null;
  permittedPc?: string | null;
  url?: string | null;
  reason?: string | null;
  period?: string | null;
  approval?: string | null;
  actor?: string | null;
  applyDateDisplay?: string | null;
  endDate?: string | null;
  applyMethod?: string | null;
  remark?: string | null;
  createdAt?: string;
}

export type WfDetailExceptionInput = Omit<WfDetailException, 'id' | 'createdAt'>;

/** 엑셀 업로드에서 등록되지 않은 행 1건 — 사용자가 누락 사유를 바로 볼 수 있게 서버가 함께 내려준다. */
export interface WfBulkUploadSkip {
  requestNo?: number;
  reqCompany?: string;
  url?: string;
  reason: string;
}

/** inserted는 실제 DB 반영 건수다(업로드 시도 행 수가 아니다). */
export interface WfBulkUploadResult {
  inserted: number;
  skipped: WfBulkUploadSkip[];
}

export interface ReportSummary {
  period: string;
  companyGroup: string;
  summaryContent: string;
}

export interface ExceptionRequest {
  id: number;
  period: string;
  companyGroup: string;
  applyDate: string | null;
  reqCompany: string | null;
  exceptType: string | null;
  category: string | null;
  reqCount: number | null;
  remark: string | null;
}

export interface PolicyStatusSummaryRow {
  exceptType: string;
  prevCount: number;
  currentCount: number;
  diffText: string;
}

export interface PolicyStatusCategoryRow {
  category: string;
  policy: '차단' | '허용' | '-';
  exceptCount: number;
}

export interface TrafficDataRow {
  recordDate: string;
  accountType: AccountType;
  blockedCount: number;
  note: string | null;
}

export interface SecurityThreatSummary {
  byAccount: { accountType: string; prevCount: number; currentCount: number; diffText: string }[];
  byCategory: { category: string; prevCount: number; currentCount: number; diffText: string }[];
}

export interface SecurityThreatTop5Row {
  category: string;
  companyName: string;
  host: string;
  blockCount: number;
}

export interface NonBusinessSiteRow {
  id: number;
  period: string;
  companyGroup: string;
  seqNo: number | null;
  companyName: string | null;
  host: string | null;
  category: string | null;
  issue: string | null;
}

export const webFilterService = {
  getPermittedCompanyGroups: async (): Promise<string[]> => {
    const r = await api.get('/web-filter/permitted-company-groups');
    return r.data;
  },

  getAvailablePeriods: async (companyGroup: string): Promise<string[]> => {
    const r = await api.get('/web-filter/available-periods', { params: { companyGroup } });
    return r.data;
  },

  getReportSummary: async (period: string, companyGroup: string): Promise<ReportSummary> => {
    const r = await api.get('/web-filter/report-summary', { params: { period, companyGroup } });
    return r.data;
  },

  saveReportSummary: async (period: string, companyGroup: string, summaryContent: string) => {
    const r = await api.post('/web-filter/report-summary', { period, companyGroup, summaryContent });
    return r.data;
  },

  getPublishStatus: async (period: string, companyGroup: string): Promise<boolean> => {
    const r = await api.get('/web-filter/publish', { params: { period, companyGroup } });
    return r.data.isPublished;
  },

  listExceptionRequests: async (params: {
    period: string;
    companyGroup: string;
    exceptType?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ExceptionRequest[]> => {
    const r = await api.get('/web-filter/exception-requests', { params });
    return r.data;
  },

  createExceptionRequest: async (data: {
    period: string;
    companyGroup: string;
    applyDate?: string;
    reqCompany?: string;
    exceptType: string;
    category?: string;
    reqCount?: number;
    remark?: string;
  }) => {
    const r = await api.post('/web-filter/exception-requests', data);
    return r.data;
  },

  deleteExceptionRequest: async (id: number) => {
    await api.delete(`/web-filter/exception-requests/${id}`);
  },

  uploadExceptionRequestsCsv: async (period: string, companyGroup: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    form.append('period', period);
    form.append('companyGroup', companyGroup);
    const r = await api.post('/web-filter/exception-requests/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },

  getPolicyStatusSummary: async (
    period: string,
    companyGroup: string,
  ): Promise<{ rows: PolicyStatusSummaryRow[]; chartItems: { exceptType: string; prev: number; current: number }[] }> => {
    const r = await api.get('/web-filter/policy-status/summary', { params: { period, companyGroup } });
    return r.data;
  },

  getPolicyStatusCategory: async (period: string, companyGroup: string): Promise<PolicyStatusCategoryRow[]> => {
    const r = await api.get('/web-filter/policy-status/category', { params: { period, companyGroup } });
    return r.data;
  },

  getTraffic: async (period: string, companyGroup: string, trafficType: TrafficType): Promise<TrafficDataRow[]> => {
    const r = await api.get('/web-filter/traffic', { params: { period, companyGroup, trafficType } });
    return r.data;
  },

  updateTrafficNote: async (payload: {
    period: string;
    companyGroup: string;
    trafficType: TrafficType;
    accountType: AccountType;
    recordDate: string;
    note: string | null;
  }): Promise<{ id: number; note: string | null }> => {
    const r = await api.patch('/web-filter/traffic-note', payload);
    return r.data;
  },

  uploadTrafficExcel: async (period: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    form.append('period', period);
    const r = await api.post('/web-filter/traffic/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },

  getSecurityThreatSummary: async (
    period: string,
    companyGroup: string,
    trafficType: TrafficType,
  ): Promise<SecurityThreatSummary> => {
    const r = await api.get('/web-filter/security-threat/summary', { params: { period, companyGroup, trafficType } });
    return r.data;
  },

  getSecurityThreatTop5: async (
    period: string,
    companyGroup: string,
    trafficType: TrafficType,
  ): Promise<SecurityThreatTop5Row[]> => {
    const r = await api.get('/web-filter/security-threat/top5', { params: { period, companyGroup, trafficType } });
    return r.data;
  },

  refreshSecurityThreatCache: async (period: string, companyGroup: string, trafficType: TrafficType) => {
    const r = await api.post('/web-filter/security-threat/refresh-cache', { period, companyGroup, trafficType });
    return r.data;
  },

  importSecurityThreatExcel: async (period: string, companyGroup: string, trafficType: TrafficType, file: File): Promise<{ imported: number }> => {
    const form = new FormData();
    form.append('file', file);
    form.append('period', period);
    form.append('companyGroup', companyGroup);
    form.append('trafficType', trafficType);
    const r = await api.post('/web-filter/security-threat/import-excel', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },

  getNonBusinessSites: async (period: string, companyGroup: string): Promise<NonBusinessSiteRow[]> => {
    const r = await api.get('/web-filter/non-business-sites', { params: { period, companyGroup } });
    return r.data;
  },

  uploadNonBusinessSitesExcel: async (period: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    form.append('period', period);
    const r = await api.post('/web-filter/non-business-sites/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },

  // ---- 예외처리 상세 관리 (IPT-534) ----

  getDetailExceptions: async (params: {
    companyGroup?: string;
    exceptGroup?: string;
    reqCompany?: string;
    search?: string;
    period?: string;
    page?: number;
    limit?: number;
    sortKey?: string;
    sortDir?: 'asc' | 'desc';
  }): Promise<{ items: WfDetailException[]; total: number }> => {
    const r = await api.get('/web-filter/detail-exceptions', { params });
    return r.data;
  },

  createDetailException: async (data: WfDetailExceptionInput): Promise<WfDetailException> => {
    const r = await api.post('/web-filter/detail-exceptions', data);
    return r.data;
  },

  updateDetailException: async (id: number, data: WfDetailExceptionInput): Promise<WfDetailException> => {
    const r = await api.put(`/web-filter/detail-exceptions/${id}`, data);
    return r.data;
  },

  deleteDetailException: async (id: number): Promise<void> => {
    await api.delete(`/web-filter/detail-exceptions/${id}`);
  },

  uploadDetailExceptionsExcel: async (file: File): Promise<WfBulkUploadResult> => {
    const form = new FormData();
    form.append('file', file);
    const r = await api.post('/web-filter/detail-exceptions/excel-upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
};
